"use client";

import React from "react";
import { ClipboardCheck, Activity, FileText, CircleCheck, Search, ChartBar } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const steps = [{
  title: "Thermal Delta Analysis",
  description: "Surgical evaluation of temperature gradients across critical infrastructure to identify exergy destruction points.",
  icon: Search,
  details: [{
    trigger: "Ambient Gradient Scan",
    content: "Real-time mapping of temperature variances across the facility floor using high-precision thermal sensors."
  }, {
    trigger: "Heatsink Efficiency",
    content: "Quantifying the heat rejection capabilities of existing cooling infrastructure against theoretical maximums."
  }, {
    trigger: "Fluid Dynamics Audit",
    content: "Measuring the velocity and turbulence of thermal transfer mediums to detect flow inefficiencies."
  }]
}, {
  title: "PUE Calibration",
  description: "Deep-layer decomposition of Power Usage Effectiveness using autonomic AI-thermal intelligence protocols.",
  icon: Activity,
  details: [{
    trigger: "IT Load Decomposition",
    content: "Separating compute energy from infrastructure overhead with 99.8% precision for accurate baseline modeling."
  }, {
    trigger: "Loss Factor Attribution",
    content: "Identifying parasitic energy draws within the power distribution network using harmonic analysis."
  }, {
    trigger: "Grid Sync Verification",
    content: "Validating the phase alignment and voltage stability of incoming power to prevent reactive energy waste."
  }]
}, {
  title: "Exergy Recovery Mapping",
  description: "Architectural blueprinting of thermal reclamation pathways to convert waste heat into usable industrial power.",
  icon: ChartBar,
  details: [{
    trigger: "Waste Heat Valuation",
    content: "Calculating the thermodynamic potential of rejected thermal energy for secondary industrial or grid use."
  }, {
    trigger: "AI Deployment Strategy",
    content: "Defining the neural network architecture required for autonomic, real-time thermal load balancing."
  }, {
    trigger: "ROI & Carbon Modeling",
    content: "Hard data modeling of recovered power capacity and projected carbon credit generation over five years."
  }]
}];
function StepItem({
  step,
  index
}: {
  step: typeof steps[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const isEven = index % 2 === 0;
  const StepIcon = step.icon;
  return <div data-section-id="4819" ref={ref} data-index={index} className={cn("relative flex flex-col md:flex-row gap-8 mb-16 last:mb-0 transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
      {}
      
      {}
      <div className={cn("w-full md:w-1/2 flex pl-12 md:pl-0", isEven ? "md:justify-end md:pr-16" : "md:justify-start md:pl-16 md:order-last")}>
        <Card className="w-full max-w-lg shadow-none rounded-none border border-border bg-card hover:border-primary transition-colors duration-300">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-mono font-bold text-primary uppercase tracking-widest">
                Protocol_0{index + 1}
              </span>
              <StepIcon className="w-4 h-4 text-muted-foreground" />
            </div>
            <CardTitle className="text-2xl font-sans font-medium text-foreground tracking-wide uppercase">
              {step.title}
            </CardTitle>
            <CardDescription className="text-sm font-sans text-muted-foreground mt-2 leading-relaxed">
              {step.description}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              {step.details.map((detail, i) => <AccordionItem key={i} value={`item-${index}-${i}`} className="border-b-border last:border-0">
                  <AccordionTrigger className="text-xs font-mono uppercase tracking-wider text-foreground/80 hover:text-primary py-4 text-left">
                    {detail.trigger}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground font-sans text-sm leading-relaxed">
                    {detail.content}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </CardContent>
        </Card>
      </div>

      {}
      <div className="absolute left-4 md:left-1/2 top-0 flex flex-col items-center h-full md:-translate-x-1/2 pointer-events-none">
        <div className={cn("w-6 h-6 rounded-none border border-border z-10 flex items-center justify-center transition-all duration-500 delay-200", isVisible ? "scale-100 bg-primary border-primary" : "scale-0 bg-muted")}>
          <div className="w-1.5 h-1.5 bg-background rounded-none" />
        </div>
      </div>
    </div>;
}
export default function TheAssessmentProcess() {
  return <section id="evaluation-criteria" className="py-24 bg-background dark text-foreground overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="text-3xl md:text-5xl font-sans font-medium tracking-widest text-foreground mb-6 uppercase">
            Evaluation Criteria
          </h2>
          <p className="text-base font-sans text-muted-foreground tracking-wide">
            Our audit engineers deploy a rigorous technical framework to isolate thermal inefficiencies and calibrate recovery potential across high-density industrial environments.
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {}
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-border md:-translate-x-1/2" />

          {}
          <div className="relative z-10">
            {steps.map((step, index) => <StepItem key={index} step={step} index={index} />)}
          </div>
          
          {}
          <div className="absolute left-4 md:left-1/2 bottom-0 w-2 h-2 bg-primary rounded-none md:-translate-x-1/2 translate-y-1/2" />
        </div>
      </div>
    </section>;
}